import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { EmailTemplatePreview } from './EmailTemplatePreview';
import { EmailContentEditor } from './EmailContentEditor';
import { AppSettingsService } from '@/services/app-settings-service';
import { ErrorBoundary } from '../ErrorBoundary';
import { 
  Users, 
  FileText, 
  Palette, 
  Send, 
  Loader2, 
  Eye, 
  Plus,
  Trash2,
  Sparkles,
  Mail,
  ArrowRight,
  ArrowLeft,
  Edit3
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  display_name?: string;
  plan?: string;
  created_at?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  html_content: string;
  variables: any;
  category: string;
  is_active: boolean;
}

interface EmailWizardProps {
  onClose?: () => void;
}

export const EmailWizard: React.FC<EmailWizardProps> = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1: Audience Selection
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');

  // Step 2: Content
  const [emailSubject, setEmailSubject] = useState('');
  const [contentType, setContentType] = useState('announcement');
  const [keyPoints, setKeyPoints] = useState<string[]>(['']);
  const [tone, setTone] = useState('professional');

  // Step 3: Design
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [generatedContent, setGeneratedContent] = useState<{
    subject: string;
    html_content: string;
    variables_used: string[];
  } | null>(null);
  
  // AI Subject Suggestions for generated content
  const [subjectOptions, setSubjectOptions] = useState<Array<{
    subject: string;
    style: string;
    explanation: string;
    length: number;
  }>>([]);

  // Step 4: Preview & Send
  const [previewHtml, setPreviewHtml] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailLogoUrl, setEmailLogoUrl] = useState('');
  
  // Template preview and content editing
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [showContentEditor, setShowContentEditor] = useState(false);

  const steps = [
    { id: 1, name: 'Audience', icon: Users, description: 'Select recipients' },
    { id: 2, name: 'Content', icon: FileText, description: 'Define content' },
    { id: 3, name: 'Design', icon: Palette, description: 'Choose template & generate' },
    { id: 4, name: 'Send', icon: Send, description: 'Preview & send' }
  ];

  // Load users for step 1
  useEffect(() => {
    loadUsers();
    loadTemplates();
  }, []);

  // Load Email Template Logo for previews and variable injection
  useEffect(() => {
    AppSettingsService.getEmailTemplateLogo()
      .then((url) => setEmailLogoUrl(url))
      .catch((e) => console.error('Failed to load email template logo URL', e));
  }, []);

  // Keep preview updated with actual logo URL
  useEffect(() => {
    if (generatedContent?.html_content) {
      let replaced = generatedContent.html_content;
      if (emailLogoUrl) {
        // Replace both logo placeholders for preview
        replaced = replaced.replace(/{{email_logo_url}}/g, emailLogoUrl);
        replaced = replaced.replace(/{{logo_url}}/g, emailLogoUrl);
      }
      setPreviewHtml(replaced);
    }
  }, [emailLogoUrl, generatedContent]);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, display_email, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const usersWithEmails = data?.map(profile => ({
        id: profile.user_id,
        email: profile.display_email || 'No email',
        display_name: profile.display_name,
        plan: 'Free', // Default plan for now
        created_at: profile.created_at
      })) || [];

      setUsers(usersWithEmails);
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    
    if (userFilter === 'all') return matchesSearch;
    if (userFilter === 'premium') return matchesSearch && user.plan !== 'Free';
    if (userFilter === 'free') return matchesSearch && user.plan === 'Free';
    
    return matchesSearch;
  });

  const addKeyPoint = () => {
    setKeyPoints([...keyPoints, '']);
  };

  const updateKeyPoint = (index: number, value: string) => {
    const newPoints = [...keyPoints];
    newPoints[index] = value;
    setKeyPoints(newPoints);
  };

  const removeKeyPoint = (index: number) => {
    if (keyPoints.length > 1) {
      setKeyPoints(keyPoints.filter((_, i) => i !== index));
    }
  };

  const generateContent = async () => {
    if (!emailSubject || keyPoints.filter(p => p.trim()).length === 0) {
      toast.error('Please provide subject and at least one key point');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-email-content', {
        body: {
          content_type: contentType,
          key_points: keyPoints.filter(p => p.trim()),
          tone,
          template_id: selectedTemplate,
          subject: emailSubject,
          user_data: {
            company_name: 'TradeBot365',
            company_url: 'https://tradebot365.com',
            support_email: 'support@tradebot365.com',
            user_name: 'Valued Trader',
            name: 'Valued Trader',
            date: new Date().toLocaleDateString('vi-VN'),
            time: new Date().toLocaleString('vi-VN'),
            current_year: new Date().getFullYear().toString(),
            unsubscribe_url: 'https://tradebot365.com/unsubscribe',
            footer_address: 'TradeBot365, TP.HCM, Vietnam'
          }
        }
      });

      if (error) throw error;

      if (data.success) {
        setGeneratedContent(data.data);
        setPreviewHtml(emailLogoUrl ? data.data.html_content.replace(/{{email_logo_url}}/g, emailLogoUrl) : data.data.html_content);
        
        // Generate subject suggestions for the generated content
        try {
          const subjectResponse = await supabase.functions.invoke('generate-subject-suggestions', {
            body: {
              content: data.data.html_content,
              content_type: contentType,
              tone: tone,
              key_points: keyPoints.filter(p => p.trim()),
              current_subject: data.data.subject,
              count: 5
            }
          });
          
          if (subjectResponse.data?.success) {
            setSubjectOptions(subjectResponse.data.suggestions);
          }
        } catch (subjectError) {
          console.error('Error generating subject suggestions:', subjectError);
          // Don't fail the whole process if subject suggestions fail
        }
        
        toast.success('Content generated successfully!');
      } else {
        throw new Error(data.error || 'Failed to generate content');
      }
    } catch (error: any) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const sendEmails = async () => {
    if (!generatedContent || selectedUsers.length === 0) {
      toast.error('Please select users and generate content first');
      return;
    }

    setSendingEmails(true);
    try {
      const selectedUserEmails = users
        .filter(user => selectedUsers.includes(user.id))
        .map(user => user.email)
        .filter(email => email && email !== 'No email' && email.includes('@')); // Filter out invalid emails

      if (selectedUserEmails.length === 0) {
        toast.error('No valid email addresses found in selected users');
        setSendingEmails(false);
        return;
      }

      // Use the current generatedContent which may have been edited
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: selectedUserEmails,
          subject: generatedContent.subject,
          html: generatedContent.html_content,
          // Don't send template_name when we have AI-generated HTML
          template_variables: {
            company_name: 'TradeBot365',
            company_url: 'https://tradebot365.com',
            support_email: 'support@tradebot365.com',
            email_logo_url: emailLogoUrl,
            logo_url: emailLogoUrl // Support both placeholders
          }
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`Emails sent successfully to ${selectedUserEmails.length} recipients`);
        onClose?.();
      } else {
        const failedEmails = data.results?.filter((r: any) => !r.success) || [];
        const successfulEmails = data.results?.filter((r: any) => r.success) || [];
        
        if (successfulEmails.length > 0) {
          toast.success(`${successfulEmails.length} emails sent successfully`);
        }
        
        if (failedEmails.length > 0) {
          const errorDetails = failedEmails.map((f: any) => `${f.email}: ${f.error}`).join('\n');
          toast.error(`${failedEmails.length} emails failed to send. Details: ${errorDetails}`);
        }
      }
    } catch (error: any) {
      console.error('Error sending emails:', error);
      toast.error('Failed to send emails: ' + error.message);
    } finally {
      setSendingEmails(false);
    }
  };

  const handleTemplatePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setShowTemplatePreview(true);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    setShowTemplatePreview(false);
  };

  const handleContentEdit = () => {
    if (generatedContent) {
      setShowContentEditor(true);
    }
  };

  const selectSubjectOption = (selectedSubject: string) => {
    if (generatedContent) {
      const updatedContent = {
        ...generatedContent,
        subject: selectedSubject
      };
      setGeneratedContent(updatedContent);
      toast.success('Subject updated!');
    }
  };

  const handleContentSave = (editedContent: { subject: string; html_content: string; variables_used: string[] }) => {
    setGeneratedContent(editedContent);
    setPreviewHtml(editedContent.html_content);
    setShowContentEditor(false);
    toast.success('Content updated successfully!');
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepComplete = (step: number) => {
    switch (step) {
      case 1: return selectedUsers.length > 0;
      case 2: return emailSubject && keyPoints.filter(p => p.trim()).length > 0;
      case 3: return generatedContent !== null;
      case 4: return previewHtml !== '';
      default: return false;
    }
  };

  return (
    <ErrorBoundary>
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Progress Steps */}
      <div className="flex justify-between items-center mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = isStepComplete(step.id);
          
          return (
            <div key={step.id} className="flex items-center">
              <div className={`flex flex-col items-center ${index < steps.length - 1 ? 'mr-4' : ''}`}>
                <div className={`
                  flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors
                  ${isActive ? 'border-primary bg-primary text-primary-foreground' : 
                    isCompleted ? 'border-green-500 bg-green-500 text-white' : 
                    'border-muted bg-muted text-muted-foreground'}
                `}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-px mx-4 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {React.createElement(steps[currentStep - 1].icon, { className: 'w-5 h-5' })}
            Step {currentStep}: {steps[currentStep - 1].name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Step 1: Audience Selection */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search users by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="premium">Premium Only</SelectItem>
                    <SelectItem value="free">Free Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {selectedUsers.length} of {filteredUsers.length} users selected
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedUsers(filteredUsers.map(u => u.id))}
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedUsers([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto border rounded-lg">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="flex items-center space-x-3 p-3 border-b last:border-b-0 hover:bg-muted/50">
                    <Checkbox
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{user.display_name || 'No name'}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <Badge variant={user.plan === 'Free' ? 'secondary' : 'default'}>
                      {user.plan || 'Free'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Content Definition */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contentType">Content Type</Label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="announcement">Announcement</SelectItem>
                      <SelectItem value="newsletter">Newsletter</SelectItem>
                      <SelectItem value="promotional">Promotional</SelectItem>
                      <SelectItem value="update">Product Update</SelectItem>
                      <SelectItem value="welcome">Welcome</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="tone">Tone</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="exciting">Exciting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Key Points</Label>
                <div className="space-y-2">
                  {keyPoints.map((point, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={point}
                        onChange={(e) => updateKeyPoint(index, e.target.value)}
                        placeholder={`Key point ${index + 1}...`}
                      />
                      {keyPoints.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeKeyPoint(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addKeyPoint}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Key Point
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Design & Generate */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <Label>Email Template</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                  {templates.map((template) => (
                    <Card 
                      key={template.id}
                      className={`cursor-pointer transition-colors ${selectedTemplate === template.id ? 'ring-2 ring-primary' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium">{template.name}</h4>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplatePreview(template);
                            }}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                        <div className="flex justify-between items-center mt-2">
                          <Badge variant="secondary" className="text-xs">{template.category}</Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplateSelect(template.id);
                            }}
                          >
                            Select
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium">Generate AI Content</h3>
                  <p className="text-sm text-muted-foreground">
                    Create professional email content based on your inputs
                  </p>
                </div>
                <Button
                  onClick={generateContent}
                  disabled={loading || !emailSubject || keyPoints.filter(p => p.trim()).length === 0}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Content
                    </>
                  )}
                </Button>
              </div>

              {generatedContent && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Generated Content</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium">Subject:</Label>
                        <p className="mt-1 p-2 bg-muted rounded">{generatedContent.subject}</p>
                        
                        {/* Subject Options */}
                        {subjectOptions.length > 0 && (
                          <div className="mt-3">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              Other Subject Options:
                            </Label>
                            <div className="mt-2 grid gap-2">
                              {subjectOptions.slice(0, 4).map((option, index) => (
                                <button
                                  key={index}
                                  onClick={() => selectSubjectOption(option.subject)}
                                  className="flex items-start justify-between p-2 text-left border rounded hover:bg-muted transition-colors"
                                >
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">{option.subject}</p>
                                    <p className="text-xs text-muted-foreground">{option.explanation}</p>
                                  </div>
                                  <div className="flex flex-col items-end gap-1 ml-2">
                                    <Badge variant="outline" className="text-xs">
                                      {option.style}
                                    </Badge>
                                    <span className={`text-xs ${
                                      option.length <= 50 ? 'text-green-600' : 'text-orange-600'
                                    }`}>
                                      {option.length}ch
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Preview:</Label>
                        <div className="mt-1 p-4 bg-muted rounded max-h-40 overflow-y-auto">
                          <div dangerouslySetInnerHTML={{ __html: (emailLogoUrl ? generatedContent.html_content.replace(/{{email_logo_url}}/g, emailLogoUrl) : generatedContent.html_content) }} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Step 4: Preview & Send */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Email Summary</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Recipients:</Label>
                      <p className="text-sm text-muted-foreground">{selectedUsers.length} users selected</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Subject:</Label>
                      <p className="text-sm">{generatedContent?.subject}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Template:</Label>
                      <p className="text-sm text-muted-foreground">
                        {selectedTemplate ? templates.find(t => t.id === selectedTemplate)?.name : 'No template'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Email Preview</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleContentEdit}
                      className="flex items-center gap-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Content
                    </Button>
                  </div>
                  <div className="border rounded-lg p-6 bg-white max-h-96 overflow-y-auto">
                    <div className="border-b pb-4 mb-4">
                      <h4 className="font-semibold text-lg">Subject: {generatedContent?.subject}</h4>
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={sendEmails}
                  disabled={sendingEmails || !generatedContent || selectedUsers.length === 0}
                  size="lg"
                >
                  {sendingEmails ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending Emails...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send {selectedUsers.length} Emails
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <div className="flex gap-2">
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          
          <Button
            onClick={nextStep}
            disabled={currentStep === 4 || !isStepComplete(currentStep)}
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Template Preview Modal */}
      <EmailTemplatePreview
        template={previewTemplate}
        isOpen={showTemplatePreview}
        onClose={() => setShowTemplatePreview(false)}
        onSelect={() => handleTemplateSelect(previewTemplate?.id || '')}
      />

      {/* Content Editor Modal */}
      {showContentEditor && generatedContent && (
        <EmailContentEditor
          initialContent={generatedContent}
          onSave={handleContentSave}
          onCancel={() => setShowContentEditor(false)}
          contentType={contentType}
          tone={tone}
          keyPoints={keyPoints.filter(p => p.trim())}
        />
      )}
    </div>
    </ErrorBoundary>
  );
};